import { MultiAndSelectAll } from '@src/components/MultiAndSelectAll';
import { useCustomerMarketAccounts } from '@src/providers';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { useEffect, useMemo, useState } from 'react';
import { setApiKeyAccountPermissions } from '../state/apiKeys/apiKeysSlice';
import { getActiveCustomerAccounts } from '../state/permissions/permissionsSlice';
import { ALL_PERMISSIONS, objectFiltersToAccountPermissions, type ObjectFilterPermissionType } from './objectFilters';

const ALL_CUSTOMER_ACCOUNTS_LABEL = 'All Customer Accounts';
const DEFAULT_PERMISSION: ObjectFilterPermissionType = 'read';

/**
 * New accounts start with View only, matching the Account Restrictions tab. A restricted user who is not
 * authorized to View an account cannot grant View on it either, so fall back to their first authorized action.
 */
function getDefaultPermissions(assignablePermissions: ObjectFilterPermissionType[]): ObjectFilterPermissionType[] {
  return assignablePermissions.includes(DEFAULT_PERMISSION) ? [DEFAULT_PERMISSION] : assignablePermissions.slice(0, 1);
}

export function ApiKeyMarketAccountsDropdown({
  apiKeyId,
  assignedAccounts,
}: {
  apiKeyId: string;
  assignedAccounts: string[];
}) {
  const [selectionsToAdd, setSelectionsToAdd] = useState<string[]>([]);
  const { getActiveCustomerMarketAccounts, customerMarketAccountsByName } = useCustomerMarketAccounts();
  const dispatch = useAppStateDispatch();
  const counterparty = useAppStateSelector(state => state.customerUserDrawer.userDetails.counterparty);
  const { customerAccounts, deletedCustomerAccounts } = useAppStateSelector(
    state => state.customerUserDrawer.permissions
  );
  const objectFilters = useAppStateSelector(
    state => state.customerUserDrawer.apiKeys.keys?.find(key => key.CustomerUserApiKeyID === apiKeyId)?.ObjectFilters
  );

  useEffect(() => {
    setSelectionsToAdd([]);
  }, [apiKeyId]);

  /** Actions the customer user is authorized for per account, or null when the user itself is unrestricted. */
  const authorizedPermissionsByAccount = useMemo(() => {
    const activeAccounts = getActiveCustomerAccounts(customerAccounts, deletedCustomerAccounts);
    if (activeAccounts.length === 0) {
      return null;
    }
    return new Map(
      activeAccounts
        .filter(account => account.permissions.length > 0)
        .map(account => [account.customerAccount, account.permissions] as const)
    );
  }, [customerAccounts, deletedCustomerAccounts]);

  // The API rejects InsertObjectFilters for non-Active market accounts and for accounts the user is not
  // authorized on, so only offer accounts that would be accepted.
  const assignablePermissionsByAccount = useMemo(() => {
    const counterpartyAccounts = counterparty ? getActiveCustomerMarketAccounts(counterparty) : [];
    return new Map(
      counterpartyAccounts
        .map(marketAccount => marketAccount.Name)
        .filter(
          account => !assignedAccounts.includes(account) && (authorizedPermissionsByAccount?.has(account) ?? true)
        )
        .map(account => [account, authorizedPermissionsByAccount?.get(account) ?? ALL_PERMISSIONS] as const)
    );
  }, [counterparty, getActiveCustomerMarketAccounts, assignedAccounts, authorizedPermissionsByAccount]);

  const availableSelections = useMemo(
    () => [...assignablePermissionsByAccount.keys()],
    [assignablePermissionsByAccount]
  );

  return (
    <MultiAndSelectAll
      data-testid="api-key-accounts-dropdown"
      placeholder="Select customer account"
      availableSelections={availableSelections}
      selectionsToAdd={selectionsToAdd}
      setSelectionsToAdd={setSelectionsToAdd}
      allSelectionsValue={availableSelections.length > 0 ? ALL_CUSTOMER_ACCOUNTS_LABEL : undefined}
      getLabel={marketAccount => customerMarketAccountsByName.get(marketAccount)?.SourceAccountID || marketAccount}
      onAddSelections={selectedAccounts => {
        const accountsToAdd = selectedAccounts.includes(ALL_CUSTOMER_ACCOUNTS_LABEL)
          ? availableSelections
          : selectedAccounts;
        dispatch(
          setApiKeyAccountPermissions({
            apiKeyId,
            accountPermissions: [
              ...objectFiltersToAccountPermissions(objectFilters),
              ...accountsToAdd.map(customerAccount => ({
                customerAccount,
                permissions: getDefaultPermissions(assignablePermissionsByAccount.get(customerAccount) ?? []),
              })),
            ],
          })
        );
        setSelectionsToAdd([]);
      }}
    />
  );
}
